// ── Мини-плеер внизу экрана ──────────────────────────────────────────────────

import { useEffect, useRef, useState } from "react";
import { SpeedPopup } from "../components/Speed";
import { useAudioTime } from "../hooks";
import { t } from "../i18n";
import Icon from "../Icon";
import { SANS } from "../themes";
import { IconButton, usePalette } from "../ui";
import { fmt } from "../utils";
import VolumeControl from "../VolumeControl";

// Мини-плеер: те же главные кнопки, что у большого плеера (±секунды, карточки,
// «Клип», «Держать — медленно», скорость). Компьютер — одна строка как у Spotify:
// название слева, управление по центру, остальное справа. Телефон — две строки.
// Свою высоту публикует в --ac-mini-h: страницы оставляют под ним место.
export function MiniPlayer({project,audio,cur:curProp=0,dur,playing,speed,vol,onToggle,onSeek,onPrevCard,onNextCard,onSpeedChange,onVolChange,onGoToPlayer,onClose,theme,
  skip=5,clipOn=false,clipLen=5,slowRate=0.6,onClip,onHoldSlow,queueCount=0,onQueue,muted=false,onMute}){
  const P=usePalette();
  const cur=useAudioTime(audio,curProp);
  const barRef=useRef(null);
  const boxRef=useRef(null);
  const [showSpeed,setShowSpeed]=useState(false);
  const [dragging,setDragging]=useState(false);
  const [slowHeld,setSlowHeld]=useState(false);
  const [phone,setPhone]=useState(()=>window.innerWidth<768);
  const [boxW,setBoxW]=useState(()=>window.innerWidth);
  useEffect(()=>{const h=()=>setPhone(window.innerWidth<768);window.addEventListener("resize",h);return()=>window.removeEventListener("resize",h);},[]);
  useEffect(()=>{
    const el=boxRef.current;if(!el)return;
    const set=()=>{document.documentElement.style.setProperty("--ac-mini-h",`${el.offsetHeight}px`);setBoxW(w=>Math.abs(w-el.offsetWidth)>8?el.offsetWidth:w);};
    set();
    const ro=typeof ResizeObserver!=="undefined"?new ResizeObserver(set):null;ro?.observe(el);
    return()=>{ro?.disconnect();};
  });
  if(!project||!dur)return null;
  const prog=dur>0?Math.min(100,(cur/dur)*100):0;
  const seekFromX=(clientX)=>{
    const r=barRef.current?.getBoundingClientRect();if(!r)return;
    onSeek&&onSeek(Math.max(0,Math.min(dur,((clientX-r.left)/r.width)*dur)));
  };
  const onBarDown=(e)=>{
    e.preventDefault();setDragging(true);seekFromX(e.clientX);
    try{e.currentTarget.setPointerCapture(e.pointerId);}catch{}
  };
  const hold=(on)=>{if(!onHoldSlow)return;setSlowHeld(on);onHoldSlow(on);};
  const iconBtn=(icon,label,onClick)=>(
    <IconButton C={P} variant="ghost" label={label} onClick={onClick} {...(typeof icon==="string"?{icon,iconSize:18}:{children:icon})}/>
  );
  const skipBtn=(sign)=>iconBtn(<span style={{fontSize:12.5,fontWeight:600}}>{sign<0?"−":"+"}{skip}{t("с")}</span>,t("{0} на {1} с",[sign<0?t("Назад"):t("Вперёд"),skip]),()=>onSeek&&onSeek(Math.max(0,Math.min(dur,cur+sign*skip))));
  const playBtn=(
    <IconButton C={P} variant="filled" size={44} label={playing?t("Пауза"):t("Играть")} onClick={onToggle} style={{borderRadius:"50%"}}>
      <Icon n={playing?"pause":"play"} s={18} style={playing?undefined:{marginLeft:2}}/>
    </IconButton>
  );
  const pill=(on)=>({display:"inline-flex",alignItems:"center",gap:6,height:36,padding:phone?"0 10px":"0 12px",borderRadius:18,fontSize:13,fontWeight:600,fontFamily:SANS,cursor:"pointer",flexShrink:0,
    border:`1px solid ${on?P.blue:P.border}`,background:on?P.blue:"transparent",color:on?"#fff":P.mid});
  const clipBtn=onClip&&(
    <button onClick={onClip} aria-pressed={clipOn} title={clipOn?t("Снять повтор"):t("Зациклить последние {0} с",[clipLen])} aria-label={clipOn?t("Снять повтор"):t("Зациклить последние {0} с",[clipLen])} style={pill(clipOn)}>
      <Icon n={clipOn?"close":"loop"} s={15}/>{!phone&&boxW>=1050&&(clipOn?t("Снять повтор"):t("Клип {0} с",[clipLen]))}
    </button>
  );
  const slowBtn=onHoldSlow&&(
    <button aria-pressed={slowHeld} title={t("Держите, чтобы слушать на {0}×",[slowRate])} aria-label={t("Держите, чтобы слушать на {0}×",[slowRate])}
      onPointerDown={e=>{e.preventDefault();try{e.currentTarget.setPointerCapture(e.pointerId);}catch{}hold(true);}}
      onPointerUp={()=>hold(false)} onPointerCancel={()=>hold(false)} onContextMenu={e=>e.preventDefault()}
      style={{...pill(slowHeld),touchAction:"none",userSelect:"none",WebkitUserSelect:"none",WebkitTouchCallout:"none",...(slowHeld?{borderColor:P.warm,background:P.warm}:{})}}>
      <Icon n="gauge" s={15}/>{!phone&&boxW>=1050&&(slowHeld?`${slowRate}×`:t("Держать {0}×",[slowRate]))}
    </button>
  );
  const speedBtn=(
    <div style={{position:"relative",flexShrink:0}}>
      <button onClick={()=>setShowSpeed(v=>!v)} title={t("Скорость")} aria-label={t("Скорость {0}×",[speed??1])} className="ac-ibtn"
        style={{height:36,minWidth:44,padding:"0 8px",borderRadius:8,border:`1px solid ${P.border}`,background:"transparent",color:P.mid,fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:SANS}}>{speed??1}×</button>
      {showSpeed&&<SpeedPopup speed={speed??1} onSelect={s=>onSpeedChange&&onSpeedChange(s)} onClose={()=>setShowSpeed(false)}/>}
    </div>
  );
  const queueBtn=onQueue&&(
    <IconButton C={P} variant="ghost" icon="list" iconSize={18} badge={queueCount||null} onClick={onQueue}
      label={queueCount?t("Очередь: {0}",[queueCount]):t("Очередь")} style={queueCount?{color:P.blueDark}:undefined}/>
  );
  const title=(
    <button onClick={onGoToPlayer} title={t("Открыть плеер")} style={{display:"block",minWidth:0,maxWidth:"100%",background:"none",border:"none",padding:0,textAlign:"left",cursor:"pointer",fontFamily:SANS,color:P.text}}>
      <span style={{display:"block",fontSize:14,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{project.name}</span>
      <span style={{display:"block",fontSize:12.5,color:P.faint,fontVariantNumeric:"tabular-nums",marginTop:1}}>{fmt(cur)} / {fmt(dur)}</span>
    </button>
  );
  const bar=(
    <div ref={barRef} onPointerDown={onBarDown} onPointerMove={e=>{if(dragging)seekFromX(e.clientX);}} onPointerUp={()=>setDragging(false)} onPointerCancel={()=>setDragging(false)}
      role="slider" aria-label={t("Перемотка")} aria-valuemin={0} aria-valuemax={Math.round(dur)} aria-valuenow={Math.round(cur)} tabIndex={0}
      onKeyDown={e=>{if(e.key==="ArrowLeft")onSeek&&onSeek(Math.max(0,cur-skip));if(e.key==="ArrowRight")onSeek&&onSeek(Math.min(dur,cur+skip));}}
      className="ac-minibar" style={{position:"relative",height:24,margin:"-4px 0",cursor:"pointer",touchAction:"none",display:"flex",alignItems:"center"}}>
      <div style={{position:"absolute",left:0,right:0,height:dragging?6:4,borderRadius:3,background:P.border,transition:"height .12s"}}>
        <div style={{width:`${prog}%`,height:"100%",borderRadius:3,background:P.blue}}/>
      </div>
      <div className="ac-minithumb" style={{position:"absolute",left:`${prog}%`,top:"50%",width:12,height:12,borderRadius:"50%",background:P.blue,transform:"translate(-50%,-50%)",opacity:dragging?1:undefined,pointerEvents:"none"}}/>
    </div>
  );
  const shell={position:"fixed",bottom:"var(--ac-nav-h, 0px)",left:"var(--ac-side-w, 0px)",right:0,zIndex:91,background:P.glassHdr,
    backdropFilter:"saturate(1.4) blur(16px)",WebkitBackdropFilter:"saturate(1.4) blur(16px)",borderTop:`1px solid ${P.border}`,userSelect:"none",fontFamily:SANS,
    paddingLeft:"env(safe-area-inset-left, 0px)",paddingRight:"env(safe-area-inset-right, 0px)",
    paddingBottom:"var(--ac-bottom-inset, env(safe-area-inset-bottom, 0px))"};
  // Полоса перемотки выглядит тонкой, а пальцем по ней попадают в полосу 32 px: она
  // заходит вверх за край мини-плеера и не наезжает на строку с названием.
  const css=<style>{`.ac-minithumb{opacity:0;transition:opacity .12s}.ac-minibar:hover .ac-minithumb,.ac-minibar:focus-visible .ac-minithumb{opacity:1}@media (hover:none){.ac-minithumb{opacity:1}}@media (pointer:coarse){.ac-minibar{height:32px!important;margin:-10px 0 -2px!important}}`}</style>;

  // В одну строку (как у Spotify) всё помещается только от ~960 px. Уже — две строки, как
  // на телефоне: раньше на планшете кнопки справа сжимали название до нуля.
  const volume=onVolChange&&(
    <VolumeControl C={P} value={vol??1} muted={muted} compact={phone||boxW<1150} width={96}
      onInput={(v)=>{if(audio)audio.volume=v;}} onChange={onVolChange} onMute={onMute}/>
  );
  if(phone||boxW<960)return(
    <div ref={boxRef} style={shell} data-surface="glass" role="region" aria-label={t("Мини-плеер")}>
      {css}
      <div style={{padding:"2px 12px 0"}}>{bar}</div>
      <div style={{display:"flex",alignItems:"center",gap:4,padding:"0 4px 0 16px"}}>
        <div style={{flex:1,minWidth:0}}>{title}</div>
        {!phone&&speedBtn}
        {!phone&&volume}
        {queueBtn}
        {iconBtn("chevronUp",t("Открыть плеер"),onGoToPlayer)}
        {iconBtn("close",t("Закрыть"),onClose)}
      </div>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:2,padding:"2px 10px 8px"}}>
        {skipBtn(-1)}
        {iconBtn("prev",t("Предыдущая карточка"),onPrevCard)}
        {playBtn}
        {iconBtn("next",t("Следующая карточка"),onNextCard)}
        {skipBtn(1)}
        {clipBtn}
        {slowBtn}
      </div>
    </div>
  );
  return(
    <div ref={boxRef} style={shell} data-surface="glass" role="region" aria-label={t("Мини-плеер")}>
      {css}
      {/* Три части: название (сжимается первым), управление (сжимается до 240), кнопки справа —
          своей ширины, поэтому никогда не наезжают на центр (раньше на всю ширину экрана наезжали). */}
      <div style={{display:"flex",alignItems:"center",gap:16,padding:"8px 16px 10px",maxWidth:1280,margin:"0 auto",boxSizing:"border-box"}}>
        <div style={{flex:"1 1 0",minWidth:140}}>{title}</div>
        <div style={{flex:"0 1 auto",display:"flex",flexDirection:"column",alignItems:"stretch",gap:2,width:boxW>=1000?400:300,minWidth:240}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
            {iconBtn("prev",t("Предыдущая карточка"),onPrevCard)}
            {skipBtn(-1)}
            {playBtn}
            {skipBtn(1)}
            {iconBtn("next",t("Следующая карточка"),onNextCard)}
          </div>
          {bar}
        </div>
        <div style={{flex:"0 0 auto",display:"flex",alignItems:"center",justifyContent:"flex-end",gap:6}}>
          {clipBtn}
          {slowBtn}
          {speedBtn}
          {volume}
          {queueBtn}
          {iconBtn("chevronUp",t("Открыть плеер"),onGoToPlayer)}
          {iconBtn("close",t("Закрыть"),onClose)}
        </div>
      </div>
    </div>
  );
}
